import { Fragment, useEffect, useMemo, useState, type ComponentType } from 'react';
import { pdf } from '@react-pdf/renderer';
import { FilePlus2, FileStack, Loader2, Plus, RotateCcw, Search, UserRoundPen, X } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { cn, showToast } from '@my/ui';
import {
  useLocations,
  useOrganisation,
  useOrganisationAccountingCategories,
  useOrganisationNominals,
  useOrganisationTaxRates,
  useOrganisationVatTreatments,
  useParties,
  useConsumeOrganisationSequence,
} from '@my/directory-api/hooks';
import { computeDueDate, describePaymentTerms, resolvePaymentTerms } from '@/utils/paymentTerms';
import { DocumentPdf } from '@/features/settings/documents/render/DocumentPdf';
import { PdfPreviewPane } from '@/features/settings/documents/builder/PdfPreviewPane';
import type { DocumentLineContext } from '@/features/settings/documents/lib/documentData';
import { useInvoiceDocumentSource } from '../lib/useInvoiceDocumentSource';
import { AdhocInvoiceBuilder } from './AdhocInvoiceBuilder';
import { ForeignVatNotice } from './ForeignVatNotice';
import { splitByPayer, type PayerUnit } from '../lib/payer';
import {
  resolveVatTreatmentLine,
  vatTreatmentLineLabel,
  type Address,
  type Organisation,
  type OrganisationAccountingCategory,
  type OrganisationNominal,
  type OrganisationTaxRate,
  type Party,
  type VatTreatmentLine,
  type VatTreatmentMatrix,
} from '@my/directory-api';
import type { Invoice } from '@my/tms-api';
import {
  tmsApi,
  useCargoDefinitions,
  useChargeCostDefinitions,
  useGenerateInvoice,
  useOrdersForInvoicing,
  useSetChargeBillTo,
  useUnapproveOrderForInvoicing,
  useWorkflowPolicies,
  type CargoDefinition,
  type ChargeCostDefinition,
} from '@my/tms-api';
import { resolveLineVatRate, type LineVatResolution } from '../lib/lineVatRate';
import {
  lineCarriesProducts,
  productsText,
  resolveLineCategory,
  type LineCategoryResolution,
} from '../lib/lineAccounting';
import { isOrderInvoiceable } from '@/features/settings/workflows/lib/invoiceability';
import type {
  GenerateInvoiceLineInput,
  InvoiceLineOverride,
  OrderForInvoicing,
  VatRoundingMethodValue,
} from '@my/tms-api';
import { RowActionsMenu } from '@/components/RowActionsMenu';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { useInvoiceViewStore } from '@/stores/invoiceView';
import { AssistantButton, CustomerGroupHeader, GroupToggle, ToolbarDivider } from './shared';

/**
 * Create-invoices tab — Approved orders with un-invoiced charges, grouped by
 * customer. "Build all" raises one invoice across a customer's orders; "Build"
 * one order. The builder lists the billable charge lines (deselectable, with
 * editable net + per-line VAT), mints an invoice number, and posts to the real
 * generate endpoint. Orders can also be sent back to For-approval.
 */
export function CreateInvoicesTab({ onOpenAssistant }: { onOpenAssistant: () => void }) {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const ordersQuery = useOrdersForInvoicing(orgId || null);
  const customersQuery = useParties(orgId, 'Customer');
  const locationsQuery = useLocations(orgId);
  const taxRatesQuery = useOrganisationTaxRates(orgId || null);
  const vatMatrixQuery = useOrganisationVatTreatments(orgId || null);
  // The VAT-rate overrides on the standard line live on these.
  const cargoDefsQuery = useCargoDefinitions(orgId || null);
  const chargeDefsQuery = useChargeCostDefinitions(orgId || null);
  const cargoDefsById = useMemo(
    () => new Map((cargoDefsQuery.data ?? []).map((d) => [d.id, d])),
    [cargoDefsQuery.data],
  );
  const chargeDefsById = useMemo(
    () => new Map((chargeDefsQuery.data ?? []).map((d) => [d.id, d])),
    [chargeDefsQuery.data],
  );
  const organisationQuery = useOrganisation(orgId);
  // Per-line nominal / category overrides choose from these. Categories only
  // while the organisation uses them (null hides the picker).
  const nominalsQuery = useOrganisationNominals(orgId || null);
  const salesNominals = useMemo(
    () => (nominalsQuery.data ?? []).filter((n) => n.type === 'Sales' && n.status === 'Active'),
    [nominalsQuery.data],
  );
  const categoriesEnabled = organisationQuery.data?.useAccountingCategories ?? false;
  const categoriesQuery = useOrganisationAccountingCategories(categoriesEnabled ? orgId || null : null);
  const accountingCategories = useMemo(
    () => (categoriesEnabled ? (categoriesQuery.data ?? []).filter((c) => c.status === 'Active') : null),
    [categoriesEnabled, categoriesQuery.data],
  );
  const unapprove = useUnapproveOrderForInvoicing();

  const roundingMethod: VatRoundingMethodValue =
    organisationQuery.data?.vatRoundingMethod ?? 'Total';

  const grouped = useInvoiceViewStore((s) => s.grouped);
  const setGrouped = useInvoiceViewStore((s) => s.setGrouped);
  const [search, setSearch] = useState('');
  const [builder, setBuilder] = useState<{ anchor: string; orderIds: string[] } | null>(null);
  // The ad-hoc builder (an invoice that bills no order), opened from the toolbar.
  const [adhocOpen, setAdhocOpen] = useState(false);

  const customersById = useMemo(() => {
    const map = new Map<string, Party>();
    for (const p of customersQuery.data ?? []) map.set(p.id, p);
    return map;
  }, [customersQuery.data]);

  const addressById = useMemo(() => {
    const map = new Map<string, Address>();
    for (const a of locationsQuery.data ?? []) map.set(a.id, a);
    return map;
  }, [locationsQuery.data]);

  // The organisation's workflow policy decides which states may be billed
  // ("Appears in invoicing from …"). It is applied here rather than server-side
  // because the rule turns on the customer's payment terms, and only the client
  // holds both the policy and the party.
  const policiesQuery = useWorkflowPolicies(orgId || null);
  const orderPolicy = useMemo(
    () => policiesQuery.data?.find((p) => p.trackKey === 'order')?.config ?? null,
    [policiesQuery.data],
  );

  // The queue is per PAYER, not per order: a charge billed to someone else
  // (or a whole order invoiced to someone else) sits under that party, and an
  // order split between two payers shows once under each.
  const allApproved = useMemo(() => (ordersQuery.data ?? []).flatMap(splitByPayer), [ordersQuery.data]);

  /**
   * Approved orders that the policy also allows to be billed. While the policy
   * is still loading nothing is hidden — dropping rows and then restoring them
   * would look like a glitch, and the server refuses anything the policy
   * forbids regardless.
   */
  const orders = useMemo(() => {
    if (!orderPolicy) return allApproved;
    return allApproved.filter((o) =>
      isOrderInvoiceable(
        orderPolicy,
        customersById.get(o.customerPartyId)?.paymentMethod,
        o.status,
      ),
    );
  }, [allApproved, orderPolicy, customersById]);

  /** How many approved orders the policy is holding back, for the empty state. */
  const withheldCount = allApproved.length - orders.length;
  const unitsByKey = useMemo(() => new Map(orders.map((o) => [o.unitKey, o])), [orders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) => {
      const customer = customersById.get(o.customerPartyId);
      const onBehalfOf = o.onBehalfOfPartyId ? customersById.get(o.onBehalfOfPartyId) : undefined;
      return (
        o.orderNumber.toLowerCase().includes(q) ||
        (customer?.name ?? '').toLowerCase().includes(q) ||
        (onBehalfOf?.name ?? '').toLowerCase().includes(q)
      );
    });
  }, [orders, search, customersById]);

  const groups = useMemo(() => {
    const map = new Map<string, PayerUnit[]>();
    for (const o of filtered) {
      const arr = map.get(o.customerPartyId);
      if (arr) arr.push(o);
      else map.set(o.customerPartyId, [o]);
    }
    return [...map.values()];
  }, [filtered]);

  const sendBack = (orderId: string) => {
    if (!orgId) return;
    unapprove.mutate(
      { organisationId: orgId, orderId },
      {
        onSuccess: () =>
          showToast({ type: 'success', message: 'Order sent back to For-approval.' }),
        onError: () => showToast({ type: 'error', message: 'Could not send the order back.' }),
      },
    );
  };

  const busy = ordersQuery.isPending || customersQuery.isPending || locationsQuery.isPending;
  const loadError = ordersQuery.error;
  const colSpan = grouped ? 13 : 14;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden pb-4">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search orders…"
            className="w-64 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
          />
        </div>
        <div className="flex items-center gap-2">
          <GroupToggle grouped={grouped} onChange={setGrouped} />
          <ToolbarDivider />
          <button
            type="button"
            onClick={() => setAdhocOpen(true)}
            disabled={adhocOpen || !orgId}
            className="inline-flex items-center gap-1 rounded-sm bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
          >
            + Adhoc invoice
          </button>
          <AssistantButton onClick={onOpenAssistant} />
        </div>
      </div>

      {adhocOpen && orgId && (
        <AdhocInvoiceBuilder
          orgId={orgId}
          customers={customersQuery.data ?? []}
          addressById={addressById}
          organisation={organisationQuery.data}
          taxRates={taxRatesQuery.data ?? []}
          vatMatrix={vatMatrixQuery.data}
          salesNominals={salesNominals}
          accountingCategories={accountingCategories}
          onClose={() => setAdhocOpen(false)}
        />
      )}

      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead>
            <tr
              className={cn(
                'sticky top-0 z-10 text-[11px] uppercase tracking-wide',
                grouped
                  ? 'border-b border-slate-300 bg-slate-50 text-slate-400'
                  : 'bg-slate-700 text-white',
              )}
            >
              <Th>Order</Th>
              {!grouped && <Th>Customer</Th>}
              <Th>Acc</Th>
              <Th>Collection point</Th>
              <Th>Delivery point</Th>
              <Th>Collected</Th>
              <Th>Delivered</Th>
              <Th>Cargo</Th>
              <Th className="text-right">Transport</Th>
              <Th className="text-right">Fuel</Th>
              <Th className="text-right">Other</Th>
              <Th className="text-right">Total</Th>
              <Th>Refs</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {!busy &&
              !loadError &&
              (grouped
                ? groups.map((g) => {
                    const customer = customersById.get(g[0]!.customerPartyId);
                    const open = builder?.anchor === g[0]!.customerPartyId;
                    return (
                      <Fragment key={g[0]!.customerPartyId}>
                        <tr>
                          <td colSpan={colSpan} className="p-0">
                            <CustomerGroupHeader
                              name={customer?.name ?? '—'}
                              code={customer?.code ?? undefined}
                              action={
                                open ? (
                                  <IconAction
                                    icon={X}
                                    label="Close"
                                    dark
                                    onClick={() => setBuilder(null)}
                                  />
                                ) : (
                                  <IconAction
                                    icon={FileStack}
                                    label="Build all"
                                    dark
                                    onClick={() =>
                                      setBuilder({
                                        anchor: g[0]!.customerPartyId,
                                        orderIds: g.map((o) => o.unitKey),
                                      })
                                    }
                                  />
                                )
                              }
                            />
                          </td>
                        </tr>
                        {g.map((o) => (
                          <OrderRow
                            key={o.unitKey}
                            o={o}
                            addressById={addressById}
                            customerCode={customer?.code ?? undefined}
                            onBehalfOfName={
                              o.onBehalfOfPartyId
                                ? (customersById.get(o.onBehalfOfPartyId)?.name ?? '—')
                                : undefined
                            }
                            onBuild={() =>
                              setBuilder({ anchor: g[0]!.customerPartyId, orderIds: [o.unitKey] })
                            }
                            onSendBack={() => sendBack(o.id)}
                          />
                        ))}
                        {open && (
                          <tr>
                            <td colSpan={colSpan} className="p-0">
                              <InvoiceBuilder
                                orgId={orgId ?? ''}
                                customer={customer}
                                addressById={addressById}
                                taxRates={taxRatesQuery.data ?? []}
                                vatMatrix={vatMatrixQuery.data}
                                cargoDefsById={cargoDefsById}
                                chargeDefsById={chargeDefsById}
                                salesNominals={salesNominals}
                                accountingCategories={accountingCategories}
                                roundingMethod={roundingMethod}
                                orgTerms={organisationQuery.data}
                                payers={customersQuery.data ?? []}
                                orders={builder!.orderIds
                                  .map((key) => unitsByKey.get(key))
                                  .filter((o): o is PayerUnit => !!o)}
                                onCancel={() => setBuilder(null)}
                                onGenerated={() => setBuilder(null)}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                : filtered.map((o) => (
                    <Fragment key={o.unitKey}>
                      <OrderRow
                        o={o}
                        addressById={addressById}
                        showCustomer
                        customerName={customersById.get(o.customerPartyId)?.name ?? '—'}
                        customerCode={customersById.get(o.customerPartyId)?.code ?? undefined}
                        onBehalfOfName={
                          o.onBehalfOfPartyId
                            ? (customersById.get(o.onBehalfOfPartyId)?.name ?? '—')
                            : undefined
                        }
                        onBuild={() => setBuilder({ anchor: o.unitKey, orderIds: [o.unitKey] })}
                        onSendBack={() => sendBack(o.id)}
                      />
                      {builder?.anchor === o.unitKey && (
                        <tr>
                          <td colSpan={colSpan} className="p-0">
                            <InvoiceBuilder
                              orgId={orgId ?? ''}
                              customer={customersById.get(o.customerPartyId)}
                              addressById={addressById}
                              taxRates={taxRatesQuery.data ?? []}
                              vatMatrix={vatMatrixQuery.data}
                              cargoDefsById={cargoDefsById}
                              chargeDefsById={chargeDefsById}
                              salesNominals={salesNominals}
                              accountingCategories={accountingCategories}
                              roundingMethod={roundingMethod}
                              orgTerms={organisationQuery.data}
                              payers={customersQuery.data ?? []}
                              orders={[o]}
                              onCancel={() => setBuilder(null)}
                              onGenerated={() => setBuilder(null)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )))}
            {busy && (
              <tr>
                <td colSpan={colSpan} className="px-3 py-10 text-center text-xs text-slate-400">
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden="true" />
                  Loading orders…
                </td>
              </tr>
            )}
            {!busy && loadError && (
              <tr>
                <td colSpan={colSpan} className="px-3 py-10 text-center text-xs text-red-600">
                  Couldn’t load orders — {loadError.message}
                </td>
              </tr>
            )}
            {!busy && !loadError && filtered.length === 0 && (
              <tr>
                <td
                  colSpan={colSpan}
                  className="px-3 py-10 text-center text-xs italic text-slate-400"
                >
                  {/* An approved order held back by the policy is the confusing
                      case — "nothing here" would send the operator looking for
                      a missing approval rather than at Settings → Workflows. */}
                  {withheldCount > 0 ? (
                    <>
                      No orders ready to invoice.
                      <span className="mt-1 block not-italic">
                        {withheldCount === 1
                          ? '1 approved order is'
                          : `${withheldCount} approved orders are`}{' '}
                        waiting to reach the state your workflow allows invoicing from.
                      </span>
                    </>
                  ) : (
                    'No approved orders waiting to be invoiced.'
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrderRow({
  o,
  addressById,
  onBuild,
  onSendBack,
  showCustomer,
  customerName,
  customerCode,
  onBehalfOfName,
}: {
  o: OrderForInvoicing;
  addressById: Map<string, Address>;
  onBuild: () => void;
  onSendBack: () => void;
  showCustomer?: boolean;
  customerName?: string;
  customerCode?: string;
  /** The order's own customer, when this row is charges someone else pays. */
  onBehalfOfName?: string;
}) {
  const totals = chargeTotals(o.charges);
  const reference = o.reference1 || o.reference2 || o.reference3 || o.reference4 || '—';
  const collection = formatAddress(addressById.get(o.collectionAddressId ?? ''));
  const delivery = formatAddress(addressById.get(o.deliveryAddressId ?? ''));
  return (
    <tr className="border-b border-slate-300 hover:bg-slate-50">
      <td className="whitespace-nowrap px-3 py-1.5 font-medium text-slate-700">
        {o.orderNumber}
        {onBehalfOfName && (
          <span
            className="block text-[10px] font-normal text-slate-400"
            title={`Order for ${onBehalfOfName} — these charges are billed here`}
          >
            for {onBehalfOfName}
          </span>
        )}
      </td>
      {showCustomer && (
        <td className="max-w-[12rem] truncate px-3 py-1.5 text-slate-700" title={customerName}>
          {customerName ?? '—'}
        </td>
      )}
      <td className="px-3 py-1.5 text-slate-500">{customerCode ?? '—'}</td>
      <td className="max-w-[12rem] truncate px-3 py-1.5 text-slate-700" title={collection}>
        {collection}
      </td>
      <td className="max-w-[12rem] truncate px-3 py-1.5 text-slate-700" title={delivery}>
        {delivery}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">
        {formatDate(o.collectedDate)}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">
        {formatDate(o.deliveredDate)}
      </td>
      <td className="max-w-[10rem] truncate px-3 py-1.5 text-slate-500" title={o.cargo ?? '—'}>
        {o.cargo ?? '—'}
      </td>
      <Money v={totals.transport} c={o.currencyIsoCode} />
      <Money v={totals.fuel} c={o.currencyIsoCode} />
      <Money v={totals.other} c={o.currencyIsoCode} />
      <Money v={totals.total} c={o.currencyIsoCode} bold />
      <td className="whitespace-nowrap px-3 py-1.5 text-slate-400">{reference}</td>
      <td className="px-3 py-1.5">
        <div className="flex items-center justify-end">
          {/* Same "⋮" as the orders list and the invoice summary — a row's
              actions live in one place across the app, so an operator learns
              it once. Both items are always listed; neither can be unavailable
              for an order sitting in this queue. */}
          <RowActionsMenu
            label={o.orderNumber}
            actions={[
              {
                id: 'build',
                label: 'Build invoice',
                icon: <FilePlus2 className="h-3.5 w-3.5 text-sky-600" />,
                onSelect: onBuild,
              },
              {
                id: 'send-back',
                label: 'Send back to approval',
                icon: <RotateCcw className="h-3.5 w-3.5 text-slate-500" />,
                onSelect: onSendBack,
              },
            ]}
          />
        </div>
      </td>
    </tr>
  );
}

/** Split the un-invoiced charge lines into the Transport / Fuel / Other columns. */
function chargeTotals(charges: OrderForInvoicing['charges']): {
  transport: number;
  fuel: number;
  other: number;
  total: number;
} {
  let transport = 0;
  let fuel = 0;
  let other = 0;
  for (const c of charges) {
    if (c.type === 'TransportCharge') transport += c.net;
    else if (c.type === 'FuelSurcharge') fuel += c.net;
    else other += c.net;
  }
  return { transport, fuel, other, total: transport + fuel + other };
}

function Money({ v, c, bold }: { v: number; c: string | null | undefined; bold?: boolean }) {
  return (
    <td
      className={cn(
        'whitespace-nowrap px-3 py-1.5 text-right tabular-nums',
        bold ? 'font-semibold text-slate-800' : 'text-slate-600',
      )}
    >
      {v === 0 ? '—' : formatMoney(v, c)}
    </td>
  );
}

type SendMode = 'send' | 'draft' | 'proforma';
type AdhocLine = {
  key: string;
  description: string;
  nominal: string;
  /** Category CODE; required while the organisation uses categories. */
  category: string;
  net: number;
  taxRatePct: number;
  rateId?: string;
};

/** Money input that always shows 2 decimals (reformats on blur; free-type while focused). */
function MoneyInput({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const [text, setText] = useState(value.toFixed(2));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(value.toFixed(2));
  }, [value, focused]);
  return (
    <input
      value={text}
      inputMode="decimal"
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        const t = e.target.value.replace(/[^\d.]/g, '');
        setText(t);
        const n = parseFloat(t);
        if (!Number.isNaN(n)) onChange(n);
      }}
      onBlur={() => {
        setFocused(false);
        const n = parseFloat(text) || 0;
        setText(n.toFixed(2));
        onChange(n);
      }}
      className="w-24 shrink-0 rounded-sm border border-slate-200 px-2 py-1 text-right text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-emerald-400"
    />
  );
}

const VAT_SOURCE_TEXT: Record<LineVatResolution['source'], string> = {
  treatment: 'VAT treatment',
  product: "the product's VAT rate on this cargo",
  cargo: "the cargo type's VAT rate",
  charge: "this charge's VAT rate",
};

/**
 * Which VAT treatment line set this charge's default — "IE-3" — and, with a
 * dot, when a product / cargo / charge override supplied the rate on the
 * standard line. Amber when the line has no schema set, or when the order's
 * products disagree on their rate; blank when no line could be told (the old
 * Standard-rate default stood).
 */
function TreatmentChip({
  matrix,
  vat,
}: {
  matrix: VatTreatmentMatrix | undefined;
  vat: LineVatResolution | undefined;
}) {
  if (!vat) return <span className="w-12 shrink-0" />;
  const label = vatTreatmentLineLabel(matrix, vat.line.ruleKey);
  const unset = !vat.rate;
  const warn = unset || vat.productsDisagree;
  const title = unset
    ? `VAT treatment ${label} applies, but no tax schema is set for it (Settings → Organisation → VAT Treatment).`
    : `VAT treatment ${label} — ${vat.rate!.name}, from ${VAT_SOURCE_TEXT[vat.source]}.` +
      (vat.productsDisagree
        ? ' The products on this order have different VAT rates — check this line.'
        : '');
  return (
    <span
      title={title}
      className={cn(
        'w-12 shrink-0 rounded-sm px-1 text-center font-mono text-[10px] font-semibold',
        warn ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500',
      )}
    >
      {label}
      {vat.source !== 'treatment' && <span className="ml-0.5">•</span>}
    </span>
  );
}

/**
 * A charge line's nominal and category, opened from its chip. Changing either
 * is an override of what the builder proposed: the line is marked ✎, and the
 * change is logged ("Overridden") when the invoice is raised. Reset puts both
 * back to the proposal.
 */
function AccountingEditor({
  nominal,
  category,
  proposedNominal,
  proposedCategory,
  salesNominals,
  categories,
  onNominal,
  onCategory,
  onReset,
}: {
  nominal: string | null;
  category: string | null;
  proposedNominal: string | null;
  proposedCategory: string | null;
  salesNominals: OrganisationNominal[];
  categories: OrganisationAccountingCategory[] | null;
  onNominal: (code: string) => void;
  onCategory: (code: string) => void;
  onReset: () => void;
}) {
  const nominalOptions = salesNominals.some((n) => n.code === nominal) || !nominal
    ? salesNominals
    : [...salesNominals, { id: `stored-${nominal}`, code: nominal, description: 'not in the list' } as OrganisationNominal];
  const edited = nominal !== proposedNominal || category !== proposedCategory;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2 rounded-sm bg-slate-50 px-2 py-1.5 pl-8 text-[11px]">
      <label className="flex items-center gap-1 text-slate-500">
        Nominal
        <select
          value={nominal ?? ''}
          onChange={(e) => onNominal(e.target.value)}
          className="rounded-sm border border-slate-200 bg-white px-1 py-0.5 text-[11px] text-slate-700"
        >
          <option value="">—</option>
          {nominalOptions.map((n) => (
            <option key={n.id} value={n.code}>
              {n.code}
              {n.description ? ` — ${n.description}` : ''}
            </option>
          ))}
        </select>
      </label>
      {categories && (
        <label className="flex items-center gap-1 text-slate-500">
          Category
          <select
            value={category ?? ''}
            onChange={(e) => onCategory(e.target.value)}
            className={cn(
              'rounded-sm border bg-white px-1 py-0.5 text-[11px]',
              category ? 'border-slate-200 text-slate-700' : 'border-amber-300 text-amber-700',
            )}
          >
            <option value="">Choose…</option>
            {categories.map((c) => (
              <option key={c.id} value={c.code}>
                {c.code} — {c.name}
              </option>
            ))}
            {category && !categories.some((c) => c.code === category) && (
              <option value={category}>{category} (not in the list)</option>
            )}
          </select>
        </label>
      )}
      <span className="text-slate-400">
        Proposed: {proposedNominal ?? '—'}
        {categories && ` · ${proposedCategory ?? '—'}`}
      </span>
      {edited && (
        <button
          type="button"
          onClick={onReset}
          className="rounded-sm px-1.5 py-0.5 font-medium text-blue-600 hover:bg-blue-50"
        >
          Reset
        </button>
      )}
    </div>
  );
}

/** Per-line VAT dropdown, sourced from the org's tax rates. */
function VatSelect({
  rates,
  value,
  onChange,
}: {
  rates: OrganisationTaxRate[];
  value: string | undefined;
  onChange: (rate: OrganisationTaxRate) => void;
}) {
  if (rates.length === 0) {
    return (
      <span className="w-32 shrink-0 text-right text-[11px] italic text-slate-400">
        No VAT rates
      </span>
    );
  }
  return (
    <select
      value={value ?? ''}
      aria-label="VAT rate"
      onChange={(e) => {
        const r = rates.find((x) => x.id === e.target.value);
        if (r) onChange(r);
      }}
      className="w-32 shrink-0 rounded-sm border border-slate-200 px-1.5 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
    >
      {rates.map((r) => (
        <option key={r.id} value={r.id}>
          {r.taxPercentage}% · {r.name}
        </option>
      ))}
    </select>
  );
}

function InvoiceBuilder({
  orgId,
  customer,
  addressById,
  taxRates,
  vatMatrix,
  cargoDefsById,
  chargeDefsById,
  salesNominals,
  accountingCategories,
  roundingMethod,
  orgTerms,
  payers,
  orders,
  onCancel,
  onGenerated,
}: {
  orgId: string;
  customer: Party | undefined;
  addressById: Map<string, Address>;
  taxRates: OrganisationTaxRate[];
  /** Settings → Organisation → VAT Treatment; decides each order's default VAT. */
  vatMatrix: VatTreatmentMatrix | undefined;
  /** For the VAT-rate overrides on the standard line (cargo, its products, the charge). */
  cargoDefsById: Map<string, CargoDefinition>;
  chargeDefsById: Map<string, ChargeCostDefinition>;
  /** Active sales nominals — the per-line nominal override chooses from these. */
  salesNominals: OrganisationNominal[];
  /** Active categories, or null when the organisation does not use them. */
  accountingCategories: OrganisationAccountingCategory[] | null;
  roundingMethod: VatRoundingMethodValue;
  /** Source of the house payment terms this customer may inherit. */
  orgTerms: Organisation | undefined;
  /** Customers a charge can be moved to ("Bill to someone else…"). */
  payers: Party[];
  orders: PayerUnit[];
  onCancel: () => void;
  onGenerated: () => void;
}) {
  const consumeSeq = useConsumeOrganisationSequence();
  const generate = useGenerateInvoice();
  const billTo = useSetChargeBillTo(orgId || null);
  const [billToOpen, setBillToOpen] = useState<string | null>(null);
  const payerOptions = useMemo(
    () =>
      payers
        .filter((p) => p.status === 'Active' && p.id !== customer?.id)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [payers, customer?.id],
  );
  const payerName = (id: string) => payers.find((p) => p.id === id)?.name ?? '—';
  // Moving a charge regroups the queue: it leaves this invoice and shows up
  // under its new payer. Nothing else on this builder is lost.
  const moveCharge = (chargeCostId: string, partyId: string | null) =>
    billTo.mutate(
      { chargeCostId, partyId },
      {
        onSuccess: () => {
          setBillToOpen(null);
          showToast({
            type: 'success',
            message: partyId
              ? `Charge moved to ${payerName(partyId)} — it now waits under them.`
              : 'Charge back with the order’s customer.',
          });
        },
        onError: (err) =>
          showToast({ type: 'error', message: err.message || 'Could not move the charge.' }),
      },
    );
  const docSource = useInvoiceDocumentSource(orgId || null);

  const currency = orders[0]?.currencyIsoCode || customer?.currencyIsoCode || 'EUR';
  const customerName = customer?.name ?? '—';

  // The VAT treatment each order falls on: the customer's VAT country and
  // where the goods go (first collection → last delivery), looked up in the
  // organisation's matrix. Null when it cannot be told — no matrix for the
  // organisation's country, no customer VAT country, or a route end unknown —
  // and then the old default stands.
  const treatmentByOrderId = useMemo(() => {
    const map = new Map<string, { line: VatTreatmentLine; rate: OrganisationTaxRate | undefined }>();
    for (const o of orders) {
      const line = resolveVatTreatmentLine(
        vatMatrix,
        customer?.taxSchemeCountryCode,
        addressById.get(o.collectionAddressId ?? '')?.countryIsoCode,
        addressById.get(o.deliveryAddressId ?? '')?.countryIsoCode,
      );
      if (!line) continue;
      const rate = line.taxRateId
        ? taxRates.find((r) => r.id === line.taxRateId && r.status === 'Active')
        : undefined;
      map.set(o.id, { line, rate });
    }
    return map;
  }, [orders, vatMatrix, customer?.taxSchemeCountryCode, addressById, taxRates]);

  // Every charge line's VAT: the order's treatment, then the overrides on the
  // standard line. Keyed by charge line id; absent when no treatment applies.
  const vatByLineId = useMemo(() => {
    const ratesById = new Map(taxRates.map((r) => [r.id, r]));
    const ordersById = new Map(orders.map((o) => [o.id, o]));
    const map = new Map<string, LineVatResolution | undefined>();
    for (const o of orders) {
      for (const ch of o.charges) {
        const order = ordersById.get(o.id);
        map.set(
          ch.id,
          resolveLineVatRate({
            treatment: treatmentByOrderId.get(o.id),
            charge: ch.chargeCostDefinitionId ? chargeDefsById.get(ch.chargeCostDefinitionId) : undefined,
            cargo: order?.cargoDefinitionId ? cargoDefsById.get(order.cargoDefinitionId) : undefined,
            productIds: order?.productIds ?? [],
            ratesById,
          }),
        );
      }
    }
    return map;
  }, [orders, treatmentByOrderId, chargeDefsById, cargoDefsById, taxRates]);

  // Every charge line's proposed nominal, category and printed products —
  // what the line goes out with unless the operator changes it.
  const accountingByLineId = useMemo(() => {
    const map = new Map<
      string,
      { nominal: string | null; category: LineCategoryResolution; products: string | null }
    >();
    for (const o of orders) {
      const cargo = o.cargoDefinitionId ? cargoDefsById.get(o.cargoDefinitionId) : undefined;
      for (const ch of o.charges) {
        const def = ch.chargeCostDefinitionId ? chargeDefsById.get(ch.chargeCostDefinitionId) : undefined;
        map.set(ch.id, {
          nominal: def?.salesNominal?.trim() || null,
          category: resolveLineCategory({ charge: def, cargo, productIds: o.productIds ?? [] }),
          products: lineCarriesProducts(def, ch.type) ? productsText(o.products ?? []) : null,
        });
      }
    }
    return map;
  }, [orders, cargoDefsById, chargeDefsById]);

  // VAT rates offered in the per-line dropdown — the org's rates for the
  // customer's tax-scheme country (fall back to all active rates if none
  // match), plus whatever the VAT treatment picked: an Irish organisation's
  // "Reverse charge" for a French customer is an IE schema, which the country
  // filter alone would hide.
  const availableRates = useMemo(() => {
    const country = customer?.taxSchemeCountryCode?.toUpperCase();
    const active = taxRates.filter((r) => r.status === 'Active');
    const forCountry = active.filter((r) => r.countryIsoCode?.toUpperCase() === country);
    const offered = new Map((forCountry.length > 0 ? forCountry : active).map((r) => [r.id, r]));
    for (const { rate } of treatmentByOrderId.values()) if (rate) offered.set(rate.id, rate);
    for (const v of vatByLineId.values()) if (v?.rate) offered.set(v.rate.id, v.rate);
    return Array.from(offered.values()).sort((a, b) => a.displayOrder - b.displayOrder);
  }, [taxRates, customer?.taxSchemeCountryCode, treatmentByOrderId, vatByLineId]);

  // Fallback per-line VAT = the Standard rate (else the first available).
  const defaultRate = useMemo(
    () => availableRates.find((r) => r.taxType === 'Standard') ?? availableRates[0],
    [availableRates],
  );
  const rateForOrder = (orderId: string) => treatmentByOrderId.get(orderId)?.rate ?? defaultRate;
  // A charge line: the treatment, then — on the standard line — the product,
  // cargo or charge override (see lib/lineVatRate).
  const rateForLine = (lineId: string, orderId: string) =>
    vatByLineId.get(lineId)?.rate ?? rateForOrder(orderId);

  // Ad-hoc lines belong to no one order: the treatment's rate when every order
  // agrees on it, else the fallback.
  const adhocRate = useMemo(() => {
    const ids = new Set(orders.map((o) => treatmentByOrderId.get(o.id)?.rate?.id ?? defaultRate?.id));
    return ids.size === 1 ? (orders[0] ? rateForOrder(orders[0].id) : defaultRate) : defaultRate;
    // rateForOrder reads only the two memos listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, treatmentByOrderId, defaultRate]);
  const defaultPct = adhocRate?.taxPercentage ?? 0;

  const lines = useMemo(
    () =>
      orders.flatMap((o) =>
        o.charges.map((ch) => ({
          ...ch,
          orderNumber: o.orderNumber,
          orderId: o.id,
          // Who the charge goes back to when its own bill-to is cleared.
          orderPayerId: o.invoiceToPartyId ?? o.onBehalfOfPartyId ?? o.customerPartyId,
        })),
      ),
    [orders],
  );

  // Default the PO to the order(s)' reference when there's exactly one distinct one.
  const distinctRefs = Array.from(
    new Set(
      orders
        .flatMap((o) => [o.reference1, o.reference2, o.reference3, o.reference4])
        .map((r) => r?.trim())
        .filter((r): r is string => !!r),
    ),
  );

  const [selected, setSelected] = useState<Set<string>>(() => new Set(lines.map((l) => l.id)));
  const [amounts, setAmounts] = useState<Record<string, number>>(() =>
    Object.fromEntries(lines.map((l) => [l.id, l.net])),
  );
  const [vat, setVat] = useState<Record<string, number>>(() =>
    Object.fromEntries(lines.map((l) => [l.id, rateForLine(l.id, l.orderId)?.taxPercentage ?? 0])),
  );
  const [rateIds, setRateIds] = useState<Record<string, string | undefined>>(() =>
    Object.fromEntries(lines.map((l) => [l.id, rateForLine(l.id, l.orderId)?.id])),
  );
  // Lines whose VAT the operator picked by hand — a late-arriving matrix or
  // rate list re-defaults every OTHER line, never these.
  const [rateTouched, setRateTouched] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    setVat((prev) => {
      const next = { ...prev };
      for (const l of lines)
        if (!rateTouched.has(l.id)) next[l.id] = rateForLine(l.id, l.orderId)?.taxPercentage ?? 0;
      return next;
    });
    setRateIds((prev) => {
      const next = { ...prev };
      for (const l of lines) if (!rateTouched.has(l.id)) next[l.id] = rateForLine(l.id, l.orderId)?.id;
      return next;
    });
    // Re-run only when the inputs to the default change, not on every touch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentByOrderId, vatByLineId, defaultRate, lines]);
  const customerHasVatNumber = !!customer?.taxNumber?.trim();
  // Hand changes to a line's nominal / category. Absent = the proposed default.
  const [nominalEdits, setNominalEdits] = useState<Record<string, string>>({});
  const [categoryEdits, setCategoryEdits] = useState<Record<string, string>>({});
  const [accountingOpen, setAccountingOpen] = useState<string | null>(null);
  const nominalFor = (id: string) =>
    nominalEdits[id] !== undefined ? nominalEdits[id] || null : (accountingByLineId.get(id)?.nominal ?? null);
  const categoryFor = (id: string) =>
    categoryEdits[id] !== undefined
      ? categoryEdits[id] || null
      : (accountingByLineId.get(id)?.category.code ?? null);
  const proposedRateId = (l: { id: string; orderId: string }) => rateForLine(l.id, l.orderId)?.id;
  const [adhoc, setAdhoc] = useState<AdhocLine[]>([]);
  const [po, setPo] = useState(() => (distinctRefs.length === 1 ? distinctRefs[0]! : ''));
  const [issueDate, setIssueDate] = useState(() => todayIso());

  // The terms that apply: this customer's own, else the organisation's house
  // terms. They drive both the default due date and the wording snapshotted
  // onto the invoice.
  const terms = useMemo(() => resolvePaymentTerms(customer, orgTerms), [customer, orgTerms]);
  const termsWording = describePaymentTerms(terms);

  const [dueDate, setDueDate] = useState(() => computeDueDate(todayIso(), terms));
  // Once the operator sets a due date by hand, changing the issue date must not
  // silently overwrite it.
  const [dueDateEdited, setDueDateEdited] = useState(false);
  const [mode, setMode] = useState<SendMode>('send');
  // Two different notes for two different readers.
  //
  // `emailMessage` is the covering note in the email that sends the invoice —
  // it names the order(s) covered, so the recipient knows what has arrived
  // without opening the attachment.
  const [emailMessage, setEmailMessage] = useState(() => {
    const nos = orders.map((o) => o.orderNumber).filter(Boolean);
    const subject =
      nos.length === 0
        ? 'transport services'
        : nos.length === 1
          ? `order ${nos[0]!}`
          : `orders ${nos.join(', ')}`;
    return `Dear ${customerName},\nPlease find attached your invoice for ${subject}.`;
  });

  // `message` is printed on the document, under the totals. A greeting belongs
  // in an email, not on a filed invoice — which is what it read like when these
  // two shared one field.
  const [message, setMessage] = useState('Thank you for your business.');

  const setLineRate = (id: string, rate: OrganisationTaxRate) => {
    setRateTouched((t) => new Set(t).add(id));
    setVat((v) => ({ ...v, [id]: rate.taxPercentage }));
    setRateIds((r) => ({ ...r, [id]: rate.id }));
  };
  const addAdhoc = () =>
    setAdhoc((prev) => [
      ...prev,
      {
        key: crypto.randomUUID(),
        description: '',
        nominal: '',
        category: '',
        net: 0,
        taxRatePct: defaultPct,
        rateId: adhocRate?.id,
      },
    ]);
  const patchAdhoc = (key: string, patch: Partial<AdhocLine>) =>
    setAdhoc((prev) => prev.map((a) => (a.key === key ? { ...a, ...patch } : a)));
  const removeAdhoc = (key: string) => setAdhoc((prev) => prev.filter((a) => a.key !== key));

  const chosen = lines.filter((l) => selected.has(l.id));
  const activeLines = [
    ...chosen.map((l) => ({ net: amounts[l.id] ?? 0, rate: vat[l.id] ?? 0 })),
    ...adhoc.map((a) => ({ net: a.net, rate: a.taxRatePct })),
  ];
  const subtotal = activeLines.reduce((s, l) => s + l.net, 0);
  const vatTotal = computeVat(activeLines, roundingMethod);
  const total = subtotal + vatTotal;

  // Transport detail for the line table's optional columns, all of it from
  // `GetOrdersForInvoicing`: the collected / delivered dates (with the planned
  // dates as fallback), both address ids, the order's four reference slots, the
  // cargo, and the trailer / container assigned to its tasks. `addressById` is
  // already resolved for the customer block, so the route costs no extra round
  // trip either.
  const contextByOrderId = useMemo(() => {
    const map = new Map<string, DocumentLineContext>();
    for (const o of orders) {
      map.set(o.id, {
        // Actual date if the task is done, else the date it was planned for. An
        // order can be billed before its tasks are marked complete, and a blank
        // date column on an invoice is worse than the scheduled date.
        collectionDate: o.collectedDate ?? o.plannedCollectionDate,
        deliveryDate: o.deliveredDate ?? o.plannedDeliveryDate,
        collectionPlace: placeName(addressById.get(o.collectionAddressId ?? '')),
        deliveryPlace: placeName(addressById.get(o.deliveryAddressId ?? '')),
        // Positional: index 0 = Reference1, matching the org's slot labels.
        references: [
          o.reference1 ?? null,
          o.reference2 ?? null,
          o.reference3 ?? null,
          o.reference4 ?? null,
        ],
        cargoProduct: o.cargo,
        cargoTemperatureC: null,
        trailer: o.trailer,
        container: o.container,
      });
    }
    return map;
  }, [orders, addressById]);

  const billingAddress = customer?.billingAddressId
    ? (addressById.get(customer.billingAddressId)?.fullAddress ?? null)
    : null;

  // ── Live preview ────────────────────────────────────────────────────────
  //
  // Built by shaping the builder's state into an Invoice-like object and pushing
  // it through the SAME `buildData` the raised invoice uses. Writing a second
  // "preview" mapping would be the obvious shortcut and the obvious bug: the
  // preview would drift from the document that actually gets stored, which is
  // the one thing a preview must never do.
  // The customer's own layout when they have one, else the organisation
  // default — the same resolution the raised invoice uses, so the preview
  // cannot show a different design from what gets printed.
  const previewTemplate = docSource.templateFor(
    mode === 'proforma' ? 'Proforma' : 'Standard',
    customer,
  );
  const previewData = useMemo(() => {
    const draftLines = [
      ...chosen.map((l, i) => {
        const net = amounts[l.id] ?? 0;
        const rate = vat[l.id] ?? 0;
        const schema = taxRates.find((r) => r.id === rateIds[l.id]);
        return {
          id: l.id,
          orderId: l.orderId,
          orderNumber: l.orderNumber,
          description: l.description,
          taxRateId: schema?.id ?? null,
          taxRateName: schema?.name ?? null,
          taxType: schema?.taxType ?? null,
          taxDescription: schema?.description ?? null,
          accountingCategoryCode: categoryFor(l.id),
          products: accountingByLineId.get(l.id)?.products ?? null,
          // The real charge type — the renderer needs it to know which line is
          // the transport charge and may therefore print the route.
          type: l.type,
          net,
          taxRatePct: rate,
          // Per-line VAT is the Item-method figure. Under Total rounding the
          // per-line cells can differ by a cent from the document VAT, which is
          // taken from `vatTotal` below and so stays exact.
          taxAmount: round2((net * rate) / 100),
          sequence: i + 1,
        };
      }),
      ...adhoc
        .filter((a) => a.description.trim().length > 0)
        .map((a, i) => ({
          id: `adhoc-${i}`,
          orderId: null,
          orderNumber: null,
          description: a.description.trim(),
          type: 'Custom',
          net: a.net,
          taxRatePct: a.taxRatePct,
          taxRateId: a.rateId ?? null,
          taxRateName: taxRates.find((r) => r.id === a.rateId)?.name ?? null,
          taxType: taxRates.find((r) => r.id === a.rateId)?.taxType ?? null,
          taxDescription: taxRates.find((r) => r.id === a.rateId)?.description ?? null,
          taxAmount: round2((a.net * a.taxRatePct) / 100),
          sequence: chosen.length + i + 1,
        })),
    ];

    const draft = {
      id: 'preview',
      // The number is minted by the sequence allocator on raise, so there is
      // nothing honest to show here yet.
      number: 'To be allocated',
      docType: mode === 'proforma' ? 'Proforma' : 'Standard',
      status: 'Draft',
      customerPartyId: customer?.id ?? '',
      customerName: customer?.name ?? '',
      customerCode: customer?.code ?? null,
      billingName: customer?.billingName ?? null,
      billingAddress,
      customerTaxNumber: customer?.taxNumber?.trim() || null,
      customerTaxCountry: customer?.taxSchemeCountryCode ?? null,
      currencyIsoCode: currency,
      issueDate: issueDate || null,
      dueDate: dueDate || null,
      paymentTerms: termsWording,
      poNumber: po.trim() || null,
      message: message.trim() || null,
      subtotal,
      vatTotal,
      total,
      exportStatus: 'Pending',
      isOverdue: false,
      lines: draftLines,
    } as unknown as Invoice;

    return docSource.buildData(draft, contextByOrderId);
    // `categoryFor` is a render-scoped reader of the state listed below
    // (categoryEdits / accountingByLineId), so those are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chosen,
    amounts,
    vat,
    rateIds,
    taxRates,
    accountingByLineId,
    nominalEdits,
    categoryEdits,
    adhoc,
    mode,
    customer,
    billingAddress,
    currency,
    issueDate,
    dueDate,
    termsWording,
    po,
    message,
    subtotal,
    vatTotal,
    total,
    docSource,
    contextByOrderId,
  ]);

  const submitting = consumeSeq.isPending || generate.isPending;

  // The basics an invoice cannot go without: a VAT rate and a sales nominal
  // on every line (the server refuses the same). Listed so the operator can see
  // what to fix, and Raise stays off until they have.
  const missingBasics = [
    ...chosen.flatMap((l) => {
      const gaps = [
        ...(rateIds[l.id] ? [] : ['VAT rate']),
        ...(nominalFor(l.id) ? [] : ['nominal']),
      ];
      return gaps.length ? [`${l.orderNumber} ${l.description}: ${gaps.join(' & ')}`] : [];
    }),
    ...adhoc
      .filter((a) => a.description.trim())
      .flatMap((a) => {
        const gaps = [...(a.rateId ? [] : ['VAT rate']), ...(a.nominal.trim() ? [] : ['nominal'])];
        return gaps.length ? [`${a.description.trim()}: ${gaps.join(' & ')}`] : [];
      }),
  ];

  const raise = async () => {
    if (missingBasics.length > 0) {
      showToast({
        type: 'error',
        message: `Every line needs a VAT rate and a nominal — missing on: ${missingBasics.slice(0, 3).join('; ')}${missingBasics.length > 3 ? '…' : ''}`,
      });
      return;
    }
    if (!orgId || !customer) {
      showToast({ type: 'error', message: 'Customer not resolved — try again.' });
      return;
    }
    // While the organisation uses categories, every line needs one — the
    // charge is what guarantees a fallback, so a gap here is a setup gap.
    if (accountingCategories) {
      const missing = [
        ...chosen.filter((l) => !categoryFor(l.id)).map((l) => l.description),
        ...adhoc.filter((a) => a.description.trim() && !a.category).map((a) => a.description.trim()),
      ];
      if (missing.length > 0) {
        showToast({
          type: 'error',
          message: `Choose an accounting category for: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '…' : ''}`,
        });
        return;
      }
    }

    const rateText = (id: string | undefined) => {
      const r = id ? taxRates.find((x) => x.id === id) : undefined;
      return r ? `${r.name} (${r.taxPercentage}%)` : null;
    };
    const sourcedLines: GenerateInvoiceLineInput[] = chosen.map((l) => {
      const acc = accountingByLineId.get(l.id);
      const overrides: InvoiceLineOverride[] = [];
      // Logged against what the builder PROPOSED, so a change made and undone
      // is not an override.
      const proposed = proposedRateId(l);
      if (rateIds[l.id] !== proposed)
        overrides.push({ field: 'Vat', from: rateText(proposed), to: rateText(rateIds[l.id]) });
      if (nominalFor(l.id) !== (acc?.nominal ?? null))
        overrides.push({ field: 'Nominal', from: acc?.nominal ?? null, to: nominalFor(l.id) });
      if (categoryFor(l.id) !== (acc?.category.code ?? null))
        overrides.push({ field: 'Category', from: acc?.category.code ?? null, to: categoryFor(l.id) });
      return {
        sourceChargeCostId: l.id,
        orderId: l.orderId,
        orderNumber: l.orderNumber,
        salesNominal: nominalFor(l.id),
        net: amounts[l.id] ?? 0,
        taxRatePct: vat[l.id] ?? 0,
        taxRateId: rateIds[l.id] ?? null,
        vatTreatmentKey: vatByLineId.get(l.id)?.line.ruleKey ?? null,
        accountingCategoryCode: categoryFor(l.id),
        products: acc?.products ?? null,
        overrides,
      };
    });
    const adhocLines: GenerateInvoiceLineInput[] = adhoc
      .filter((a) => a.description.trim().length > 0)
      .map((a) => ({
        sourceChargeCostId: null,
        orderId: null,
        orderNumber: null,
        description: a.description.trim(),
        type: 'Custom',
        salesNominal: a.nominal.trim() || null,
        net: a.net,
        taxRatePct: a.taxRatePct,
        taxRateId: a.rateId ?? null,
        accountingCategoryCode: a.category || null,
      }));
    const payloadLines = [...sourcedLines, ...adhocLines];
    if (payloadLines.length === 0) {
      showToast({ type: 'error', message: 'Add at least one charge to invoice.' });
      return;
    }

    try {
      // A proforma must NOT burn an invoice number — the invoice run has to
      // stay gapless for audit, and the real invoice still follows this
      // document. Each doc type draws on its own configured sequence.
      const seq = await consumeSeq.mutateAsync({
        organisationId: orgId,
        type: mode === 'proforma' ? 'Proforma' : 'Invoice',
      });
      const created = await generate.mutateAsync({
        organisationId: orgId,
        number: seq.formatted,
        docType: mode === 'proforma' ? 'Proforma' : 'Standard',
        customerPartyId: customer.id,
        customerName: customer.name,
        customerCode: customer.code,
        billingName: customer.billingName,
        billingAddress,
        customerTaxNumber: customer.taxNumber?.trim() || null,
        customerTaxCountry: customer.taxSchemeCountryCode ?? null,
        currencyIsoCode: currency,
        issueDate: issueDate || null,
        dueDate: dueDate || null,
        paymentTerms: termsWording,
        poNumber: po.trim() || null,
        message: message.trim() || null,
        emailMessage: emailMessage.trim() || null,
        customerPaymentMethod: customer.paymentMethod ?? null,
        issueNow: mode === 'send',
        roundingMethod,
        lines: payloadLines,
      });
      // Best-effort: render the PDF through the organisation's default layout
      // and store it in blob. The invoice already exists if this fails — it can
      // be re-rendered on demand.
      try {
        const template = docSource.templateFor(created.docType, customer);
        if (template) {
          const blob = await pdf(
            <DocumentPdf
              template={template}
              data={docSource.buildData(created, contextByOrderId)}
            />,
          ).toBlob();
          await tmsApi.uploadInvoicePdf(created.id, blob);
        }
      } catch {
        // ignore — invoice is saved; PDF can be regenerated later
      }
      // Name the document by what it actually is — "Invoice PRO-000042 saved as
      // proforma" reads as though an invoice number was consumed, which is the
      // very thing this flow must not do.
      const label =
        mode === 'send' ? 'raised & issued' : mode === 'draft' ? 'saved as draft' : 'saved';
      const kind = mode === 'proforma' ? 'Proforma' : 'Invoice';
      showToast({
        type: 'success',
        message: `${kind} ${created.number} ${label} for ${customerName}.`,
      });
      onGenerated();
    } catch (err) {
      // Surface the API's own message: switching proformas onto their own
      // sequence makes "No Proforma sequence configured for this
      // organisation" reachable, and a generic retry prompt would send the
      // operator round in circles instead of to Settings → Sequences.
      showToast({
        type: 'error',
        message:
          err instanceof Error && err.message
            ? err.message
            : 'Could not generate the invoice — please try again.',
      });
    }
  };

  return (
    <div className="border-y-2 border-emerald-200 bg-emerald-50/40 p-3">
      <p className="mb-2 rounded-md bg-emerald-100/70 px-3 py-1.5 text-[11px] font-medium text-emerald-800">
        Invoice for {customerName} — {orders.length} order{orders.length === 1 ? '' : 's'} ·{' '}
        {lines.length} charge lines. Deselect lines to exclude; set the VAT rate per line.
      </p>
      {missingBasics.length > 0 && (
        <div className="mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-[11px] text-red-800">
          <p className="font-semibold">
            Can't raise yet — every line needs a VAT rate and a nominal.
          </p>
          <ul className="mt-0.5 list-disc pl-4">
            {missingBasics.slice(0, 5).map((m) => (
              <li key={m}>{m}</li>
            ))}
            {missingBasics.length > 5 && <li>…and {missingBasics.length - 5} more</li>}
          </ul>
          <p className="mt-0.5 text-red-700">
            Set them on the line (the chip beside the VAT opens the nominal), or fix the defaults in
            Settings → Organisation: Tax Schemas, VAT Treatment and Charges &amp; Costs.
          </p>
        </div>
      )}
      {customer && !customerHasVatNumber && (
        <p className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] font-medium text-amber-800">
          {customerName} has no VAT number — check the VAT on each line before sending.
        </p>
      )}
      <ForeignVatNotice currency={currency} orgCurrency={orgTerms?.currencyIsoCode} vatTotal={vatTotal} />
      {/* Charges take the slack; the settings column is fixed and the preview
          grows with the viewport — it is the column that benefits from width,
          and the charge rows are short. */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_17rem] xl:grid-cols-[1fr_16rem_28rem] 2xl:grid-cols-[1fr_16rem_34rem]">
        <div className="rounded-md border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-300 px-3 py-1.5">
            <span className="text-xs font-semibold text-slate-700">Charges — {customerName}</span>
            <span className="text-[10px] text-slate-400">{lines.length} lines</span>
          </div>
          <ul className="divide-y divide-slate-100">
            {lines.map((l) => {
              const on = selected.has(l.id);
              const lineTotal = (amounts[l.id] ?? 0) * (1 + (vat[l.id] ?? 0) / 100);
              const acc = accountingByLineId.get(l.id);
              const nominal = nominalFor(l.id);
              const category = categoryFor(l.id);
              const edited =
                nominal !== (acc?.nominal ?? null) || category !== (acc?.category.code ?? null);
              const accountingMissing = !nominal || (!!accountingCategories && !category);
              const open = accountingOpen === l.id;
              return (
                <li key={l.id} className={cn('px-3 py-1.5', !on && 'opacity-50')}>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (next.has(l.id)) next.delete(l.id);
                        else next.add(l.id);
                        return next;
                      })
                    }
                    className="accent-emerald-600"
                  />
                  <span className="w-24 shrink-0 text-[11px] tabular-nums text-emerald-700">
                    {l.orderNumber}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-800">
                    {l.description}
                    {acc?.products && (
                      <span className="block truncate text-[10px] font-normal text-slate-500">
                        {acc.products}
                      </span>
                    )}
                  </span>
                  <MoneyInput
                    value={amounts[l.id] ?? 0}
                    onChange={(n) => setAmounts((a) => ({ ...a, [l.id]: n }))}
                  />
                  <button
                    type="button"
                    onClick={() => setAccountingOpen(open ? null : l.id)}
                    title={
                      `Nominal ${nominal ?? '—'}` +
                      (accountingCategories ? ` · category ${category ?? '—'}` : '') +
                      (edited ? ' — changed by hand' : '') +
                      (acc?.category.productsDisagree
                        ? ' · the products on this order have different categories — check'
                        : '')
                    }
                    className={cn(
                      'w-24 shrink-0 truncate rounded-sm px-1 py-0.5 text-left font-mono text-[10px] font-semibold',
                      accountingMissing || acc?.category.productsDisagree
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-violet-50 text-violet-700 hover:bg-violet-100',
                    )}
                  >
                    {nominal ?? '—'}
                    {accountingCategories && ` · ${category ?? '—'}`}
                    {edited && ' ✎'}
                  </button>
                  <TreatmentChip matrix={vatMatrix} vat={vatByLineId.get(l.id)} />
                  <VatSelect
                    rates={availableRates}
                    value={rateIds[l.id]}
                    onChange={(r) => setLineRate(l.id, r)}
                  />
                  <span className="w-20 shrink-0 text-right text-xs font-semibold tabular-nums text-slate-800">
                    {formatMoney(lineTotal, currency)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setBillToOpen(billToOpen === l.id ? null : l.id)}
                    aria-label="Bill to someone else"
                    title="Bill to someone else…"
                    className={cn(
                      'shrink-0 rounded-sm p-0.5 hover:bg-slate-100',
                      billToOpen === l.id ? 'text-sky-600' : 'text-slate-300 hover:text-slate-600',
                    )}
                  >
                    <UserRoundPen className="h-3.5 w-3.5" />
                  </button>
                </div>
                {billToOpen === l.id && (
                  <div className="ml-6 mt-1 flex flex-wrap items-center gap-2 rounded-sm bg-sky-50 px-2 py-1 text-[11px] text-slate-600">
                    <span>Bill this charge to</span>
                    <select
                      defaultValue=""
                      disabled={billTo.isPending}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) return;
                        moveCharge(l.id, v === '__order' ? null : v);
                      }}
                      className="min-w-[14rem] rounded-sm border border-slate-300 bg-white px-1.5 py-0.5 text-[11px]"
                    >
                      <option value="">Choose a customer…</option>
                      {l.billToPartyId && l.orderPayerId !== customer?.id && (
                        <option value="__order">Back to the order — {payerName(l.orderPayerId)}</option>
                      )}
                      {payerOptions
                        .filter((p) => !(l.billToPartyId && p.id === l.orderPayerId))
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                            {p.code ? ` (${p.code})` : ''}
                          </option>
                        ))}
                    </select>
                    {billTo.isPending && <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
                    <span className="text-slate-400">
                      It leaves this invoice and waits under them, in {currency}.
                    </span>
                  </div>
                )}
                {open && (
                  <AccountingEditor
                    nominal={nominal}
                    category={category}
                    proposedNominal={acc?.nominal ?? null}
                    proposedCategory={acc?.category.code ?? null}
                    salesNominals={salesNominals}
                    categories={accountingCategories}
                    onNominal={(v) => setNominalEdits((e) => ({ ...e, [l.id]: v }))}
                    onCategory={(v) => setCategoryEdits((e) => ({ ...e, [l.id]: v }))}
                    onReset={() => {
                      setNominalEdits(({ [l.id]: _n, ...rest }) => rest);
                      setCategoryEdits(({ [l.id]: _c, ...rest }) => rest);
                    }}
                  />
                )}
                </li>
              );
            })}
            {adhoc.map((a) => (
              <li key={a.key} className="flex items-center gap-2 px-3 py-1.5">
                <button
                  type="button"
                  onClick={() => removeAdhoc(a.key)}
                  aria-label="Remove charge"
                  title="Remove"
                  className="text-slate-300 hover:text-red-500"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <span className="w-14 shrink-0 text-[11px] font-medium text-violet-600">
                  Ad-hoc
                </span>
                <input
                  value={a.description}
                  onChange={(e) => patchAdhoc(a.key, { description: e.target.value })}
                  placeholder="Description…"
                  className="min-w-0 flex-1 rounded-sm border border-slate-200 px-2 py-1 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                />
                <input
                  value={a.nominal}
                  onChange={(e) => patchAdhoc(a.key, { nominal: e.target.value })}
                  placeholder="Nominal"
                  aria-label="Sales nominal"
                  className="w-24 shrink-0 rounded-sm border border-slate-200 px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                />
                {accountingCategories && (
                  <select
                    value={a.category}
                    aria-label="Accounting category"
                    onChange={(e) => patchAdhoc(a.key, { category: e.target.value })}
                    className={cn(
                      'w-24 shrink-0 rounded-sm border px-1 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400',
                      a.category ? 'border-slate-200 text-slate-700' : 'border-amber-300 text-amber-700',
                    )}
                  >
                    <option value="">Category…</option>
                    {accountingCategories.map((c) => (
                      <option key={c.id} value={c.code}>
                        {c.code} — {c.name}
                      </option>
                    ))}
                  </select>
                )}
                <MoneyInput value={a.net} onChange={(n) => patchAdhoc(a.key, { net: n })} />
                <VatSelect
                  rates={availableRates}
                  value={a.rateId}
                  onChange={(r) => patchAdhoc(a.key, { taxRatePct: r.taxPercentage, rateId: r.id })}
                />
                <span className="w-20 shrink-0 text-right text-xs font-semibold tabular-nums text-slate-800">
                  {formatMoney(a.net * (1 + a.taxRatePct / 100), currency)}
                </span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={addAdhoc}
            className="m-2 inline-flex items-center gap-1 rounded-sm border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-50"
          >
            <Plus className="h-3 w-3" /> Add charge
          </button>
        </div>

        <div className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-3">
          <div>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Invoice total
              </p>
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                {mode === 'proforma' ? 'Proforma' : 'Standard'}
              </span>
            </div>
            <div className="mt-1 flex justify-between text-xs text-slate-500">
              <span>Subtotal ex-VAT</span>
              <span className="tabular-nums">{formatMoney(subtotal, currency)}</span>
            </div>
            <div className="flex justify-between text-xs text-slate-500">
              <span>VAT</span>
              <span className="tabular-nums">{formatMoney(vatTotal, currency)}</span>
            </div>
            <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 text-sm font-bold text-slate-900">
              <span>Total</span>
              <span className="tabular-nums">{formatMoney(total, currency)}</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-[11px] font-medium text-slate-500">
              Issue date
              <input
                type="date"
                value={issueDate}
                onChange={(e) => {
                  setIssueDate(e.target.value);
                  // Keep the due date in step with the terms while it is still
                  // the derived value.
                  if (!dueDateEdited && e.target.value) {
                    setDueDate(computeDueDate(e.target.value, terms));
                  }
                }}
                className="mt-0.5 w-full rounded-sm border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            </label>
            <label className="block text-[11px] font-medium text-slate-500">
              Due date
              <input
                type="date"
                value={dueDate}
                onChange={(e) => {
                  setDueDate(e.target.value);
                  setDueDateEdited(true);
                }}
                className="mt-0.5 w-full rounded-sm border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
              <span className="mt-0.5 block text-[10px] font-normal text-slate-400">
                {dueDateEdited ? 'Set by hand' : termsWording}
                {terms.inherited ? ' (organisation default)' : ''}
              </span>
            </label>
            <label className="block text-[11px] font-medium text-slate-500">
              PO number
              <input
                value={po}
                onChange={(e) => setPo(e.target.value)}
                className="mt-0.5 w-full rounded-sm border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            </label>
            <label className="block text-[11px] font-medium text-slate-500">
              Email message
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={2}
                className="mt-0.5 w-full resize-none rounded-sm border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
              <span className="mt-0.5 block text-[10px] font-normal text-slate-400">
                Sent with the invoice. Not printed on it.
              </span>
            </label>
            <label className="block text-[11px] font-medium text-slate-500">
              Invoice message
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
                className="mt-0.5 w-full resize-none rounded-sm border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
              <span className="mt-0.5 block text-[10px] font-normal text-slate-400">
                Printed on the document, under the totals.
              </span>
            </label>
          </div>

          <div className="space-y-1 text-[11px] text-slate-600">
            <Radio
              name="mode"
              checked={mode === 'send'}
              onChange={() => setMode('send')}
              label="Generate & issue now"
              hint="Marks the invoice Issued"
            />
            <Radio
              name="mode"
              checked={mode === 'draft'}
              onChange={() => setMode('draft')}
              label="Generate as draft"
              hint="Hold in Invoice summary"
            />
            <Radio
              name="mode"
              checked={mode === 'proforma'}
              onChange={() => setMode('proforma')}
              label="Save as proforma"
              hint="Non-binding preview"
            />
          </div>

          <div className="flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={raise}
              disabled={submitting || chosen.length === 0 || missingBasics.length > 0}
              title={missingBasics.length > 0 ? 'Every line needs a VAT rate and a nominal first.' : undefined}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              Raise invoice
            </button>
          </div>
        </div>

        {/* Preview — the document as it will be raised, rendered through the
            organisation's default layout. Hidden below xl, where three columns
            would squeeze the charge list rather than help. */}
        <div className="hidden min-w-0 flex-col xl:flex">
          <div className="flex items-center justify-between px-1 pb-1">
            <span className="text-xs font-semibold text-slate-700">Preview</span>
            <span className="truncate text-[10px] text-slate-400" title={previewTemplate?.name}>
              {previewTemplate?.name ?? 'No layout'}
            </span>
          </div>
          {previewTemplate ? (
            <div className="min-h-0 flex-1 overflow-hidden rounded-md border border-slate-300 bg-white">
              <PdfPreviewPane template={previewTemplate} data={previewData} className="h-[42rem]" />
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-slate-300 bg-white p-3 text-[11px] text-slate-500">
              No document layout is set as the default for invoices, so there is nothing to preview.
              Set one in Settings → Documents.
            </div>
          )}
          <p className="mt-1 px-1 text-[10px] leading-snug text-slate-400">
            The invoice number is allocated when you raise it.
          </p>
        </div>
      </div>
    </div>
  );
}

function Radio({
  name,
  checked,
  onChange,
  label,
  hint,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  hint: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2">
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        className="mt-0.5 accent-emerald-600"
      />
      <span>
        <span className="font-medium text-slate-700">{label}</span>
        <span className="block text-[10px] text-slate-400">{hint}</span>
      </span>
    </label>
  );
}

/** Icon button with a hover tooltip. */
function IconAction({
  icon: Icon,
  label,
  onClick,
  dark,
  tone,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  dark?: boolean;
  tone?: 'slate';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded-md',
        dark
          ? 'text-emerald-300 hover:bg-white/10 hover:text-emerald-200'
          : tone === 'slate'
            ? 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
            : 'text-emerald-600 hover:bg-emerald-50',
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <th className={cn('px-3 py-1.5 text-left font-semibold', className)}>{children}</th>;
}

/**
 * Place for a route cell, worded the same way the order's task header words it:
 * "Dunnes Stores, Limerick, IE". Name first because that is what identifies the
 * site to the customer; the country is the address record's own
 * `countryIsoCode`, so a wrong country on the document means a wrong country on
 * the address.
 *
 * Each part is optional — a site with no city still prints its name and country
 * rather than leaving a stray comma.
 */
function placeName(a: Address | undefined): string | null {
  if (!a) return null;
  const parts = [a.name?.trim(), a.city?.trim(), a.countryIsoCode?.trim()].filter(
    (p): p is string => Boolean(p),
  );
  return parts.length > 0 ? parts.join(', ') : null;
}

function formatAddress(a: Address | undefined): string {
  if (!a) return '—';
  const parts = [a.name?.trim(), a.city?.trim()].filter((p): p is string => !!p);
  return parts.length > 0 ? parts.join(', ') : a.fullAddress?.trim() || '—';
}

function formatMoney(v: number, iso: string | null | undefined): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: iso || 'EUR' }).format(
      v,
    );
  } catch {
    return v.toFixed(2);
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

function todayIso(): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * VAT total per the org's rounding method — mirrors the server so the displayed
 * total matches what's stored. `Item` rounds each line; `Total` rounds once per
 * rate band on the summed nets.
 */
function computeVat(
  lines: { net: number; rate: number }[],
  method: VatRoundingMethodValue,
): number {
  if (method === 'Item') {
    return round2(lines.reduce((s, l) => s + round2((l.net * l.rate) / 100), 0));
  }
  const byRate = new Map<number, number>();
  for (const l of lines) byRate.set(l.rate, (byRate.get(l.rate) ?? 0) + l.net);
  let vat = 0;
  for (const [rate, net] of byRate) vat += round2((net * rate) / 100);
  return round2(vat);
}
